import React, { useState, useEffect, useRef } from "react"
import PropTypes from "prop-types"
import { directAxiosInstance } from "@lib/api"
import { getAssetPath, Validate, excludeCorporateRedirection, sanitizer } from "@commonUtils"
import InputField from "@components/nonDLSComponents/InputField/InputField.js"
import Button from "@components/Button/Button"
import Dialog from "@components/Dialog/Dialog.js"
import css from "../Auth.scss"
import ConditionalLink from "@wrappers/ConditionalLink/ConditionalLink"
import { setConflictCaseInfo, updateAuthenticationInfo, mergeAccountInfo, setDefault } from "../reducer"
import { setDefaultAuthConfig } from "@containers/App/reducer"
import { clickContinuePopupGA, enterPhoneNumberForEmailFlowGA, phoneConflictGA } from "../GAEvents"
import { useDispatch, useSelector } from "react-redux"
import ImageLoader from "@components/ImageLoader/ImageLoader"
import MergeAccount from "./MergeAccount"
import ARIAButton from "@wrappers/ARIAButton/ARIAButton"

const PhoneConflict = ({ forwardHandler, onSuccess, state, localDispatch }) => {
    const dispatch = useDispatch()
    const { authConfig } = useSelector((state) => state.shellReducer)
    const { info, conflictInfo, mergeAccountInformation } = state
    const [phone, setPhone] = useState(info.phone || conflictInfo.number || "")
    const [error, setError] = useState(null)
    const [ctaLabel, setCtaLabel] = useState("Continue")
    const [iconUrl, setIconUrl] = useState(null)
    // we are using "active" as a flag so that untill the request is completed, we do not allow multiple request
    // on multiple clicks on cta
    const [active, setActive] = useState(false)
    const [warningDialog, toggleWarningDialog] = useState({ flag: false })
    const [focused, setFocused] = useState(false)
    const onFocus = () => {
        setFocused(true)
    }
    const onBlur = () => {
        if (phone === "") {
            setFocused(false)
        }
    }
    /**
     * @description when user come on this screen cursor should be on inputField .
     */
    const inputRef = useRef(null)
    useEffect(() => {
        setFocused(true)
        inputRef.current.focus()
    }, [])
    /**
     *
     * @description Function to submit the form by clicking on enter
     */
    const onKeyUp = (event) => {
        if (event.key === "Enter") {
            handleSubmit()
        }
    }
    /**
     * @description Function to check valid input
     */
    const errorCheck = () => {
        setError(null)
        const errs = Validate({
            phone: phone,
        })
        setError(errs)
        return errs
    }

    /**
     * @description Function to merge user info
     */

    const mergeAccount = () => {
        directAxiosInstance()
            .get("/auth/merge_data", {
                params: {
                    email: info.email,
                    number: phone,
                },
            })
            .then((data) => {
                localDispatch(updateAuthenticationInfo({ phone: phone }))
                localDispatch(
                    mergeAccountInfo({
                        flag: true,
                        header: data?.data?.title,
                        description: data?.data?.sub_title,
                        imageUrl: data?.data?.icon,
                        ctaLabel: data?.data?.cta,
                        heading: data?.data?.sections?.header,
                        steps: data?.data?.sections?.items?.map((val) => {
                            return {
                                heading: val?.title,
                                subHeading: val?.sub_title,
                                imageUrl: val?.icon,
                            }
                        }),
                    })
                )
                toggleWarningDialog({ flag: false })
                clickContinuePopupGA()
            })
    }

    /**
     * @description The following function handles phone conflict flow and complete login flow
     */

    const handleSubmit = () => {
        const errs = errorCheck()

        if (!errs && !active) {
            setActive(true)
            setIconUrl(getAssetPath("svgs/icons/loading-wheel_24px_background.svg"))
            directAxiosInstance()
                .post(
                    conflictInfo.conflict ? "auth/email/create_token_conflict" : "auth/email/complete_login",
                    {
                        number: phone,
                        email: info.email,
                    }
                )
                .then((res) => {
                    if (res && res.data) {
                        const userData = res?.data?.user_data
                        const userFlags = res?.data?.user_flags
                        if (userData) {
                            enterPhoneNumberForEmailFlowGA("Success")
                            const corporateRedirectionUrl = excludeCorporateRedirection()
                            onSuccess(userData, corporateRedirectionUrl)
                        } else {
                            if (userFlags.is_otp_required) {
                                if (conflictInfo.conflict) {
                                    phoneConflictGA("Success")
                                    localDispatch(setConflictCaseInfo({ number: phone }))
                                } else {
                                    enterPhoneNumberForEmailFlowGA("Success")
                                    localDispatch(updateAuthenticationInfo({ phone: phone }))
                                }
                                forwardHandler("OtpConflict")
                            }
                        }
                    }
                    setIconUrl(null)
                    setCtaLabel("Continue")
                    setActive(false)
                })
                .catch((err) => {
                    setIconUrl(null)
                    setCtaLabel("Continue")
                    const errMsg = err?.data?.error?.message || "Server error"
                    conflictInfo.conflict ? phoneConflictGA("Error") : enterPhoneNumberForEmailFlowGA("Error")
                    if (err?.data?.error?.errors?.[0]?.cta_type) {
                        const dialogData = err.data.error.errors[0]
                        if (dialogData?.cta_type === "NON_MERGE") {
                            toggleWarningDialog({
                                flag: true,
                                heading: dialogData.heading,
                                message: dialogData.message,
                                primaryCtaLabel: dialogData.confirm_cta,
                                primaryAction: () => {
                                    toggleWarningDialog({ flag: false })
                                },
                            })
                        } else {
                            toggleWarningDialog({
                                infoIconUrl: dialogData.info_icon,
                                closeButton: true,
                                closeAction: () => {
                                    toggleWarningDialog({ flag: false })
                                },
                                infoIcon: true,
                                infoIconAction: () => mergeAccount(),
                                flag: true,
                                heading: dialogData.heading,
                                message: dialogData.message,
                                primaryCtaLabel: dialogData.confirm_cta,
                                primaryAction: () => mergeAccount(),
                                secondaryCtaLabel: dialogData.cancel_cta,
                                secondaryAction: () => {
                                    toggleWarningDialog({ flag: false })
                                },
                            })
                        }
                    }
                    setError({ phone: errMsg })
                    setActive(false)
                })
        }
    }

    return (
        <>
            <div className={css.rightContainer}>
                {!authConfig.restrictUserFromClosingAuth && (
                    <div className={`flex ${css.crossIcon} ${css.cursorPointer}`}>
                        <div>
                            <ARIAButton
                                onClick={() => {
                                    dispatch(setDefaultAuthConfig())
                                    localDispatch(setDefault())
                                }}
                                ariaLabel={"Close phone number conflict popup"}
                            >
                                <img
                                    src={getAssetPath("svgs/icons/cross_18px_primary.svg")}
                                    alt={"Close icon"}
                                />
                            </ARIAButton>
                        </div>
                    </div>
                )}
                <div className={css.authContainer}>
                    <div className={` ${css.paddingAuth} flex flexColumn justifyBetween`}>
                        <div>
                            <span className={css.heading}>Enter your mobile number</span>
                            <div className={`smallRegular textSecondary ${css.subHeadingMargin}`}>
                                We are almost ready! Just need your email id to keep your account extra safe
                            </div>

                            <div className="marginBottom24">
                                <InputField
                                    name="phone"
                                    label="Enter your phone number"
                                    type="tel"
                                    value={phone}
                                    onChange={setPhone}
                                    onBlur={() => onBlur()}
                                    focused={focused}
                                    onFocus={() => onFocus()}
                                    alertType={"error"}
                                    alertMessage={error && error.phone}
                                    borderType={"primary"}
                                    onKeyUp={(e) => onKeyUp(e)}
                                    ref={inputRef}
                                />
                            </div>
                        </div>

                        <div className="flexColumn alignCenter justifyCenter ">
                            <Button
                                label={!iconUrl ? ctaLabel : null}
                                iconUrl={iconUrl}
                                isDisabled={iconUrl ? true : false}
                                onClick={() => handleSubmit()}
                            />
                            <div className="marginTop-16 flexColumn alignCenter justifyCenter">
                                <div className={`${css.footerSubText} flexColumn alignCenter`}>
                                    <div className="textTertiary flexColumn alignCenter">
                                        By signing up, you agree to our
                                        <div>
                                            <span
                                                className={`textTertiary ${css.underlineText}`}
                                                onClick={() => {
                                                    dispatch(setDefaultAuthConfig())
                                                    window.location.href = "/tnc"
                                                }}
                                            >
                                                Terms and Conditions
                                            </span>
                                            <span className={css.marginBoth4}>&</span>
                                            <span
                                                className={`textTertiary ${css.underlineText}`}
                                                onClick={() => {
                                                    dispatch(setDefaultAuthConfig())
                                                    window.location.href = "/PrivacyPolicy"
                                                }}
                                            >
                                                Privacy policy
                                            </span>
                                        </div>
                                    </div>
                                    <ConditionalLink
                                        className={` ${css.marginFooter} smallSemiBold textAction ${css.cursorPointer}`}
                                        onClick={() => forwardHandler("NeedHelp")}
                                    >
                                        <span>Need Help?</span>
                                        <span className={css.marginLeft4}> Get in touch</span>
                                    </ConditionalLink>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <Dialog isOpen={warningDialog?.flag}>
                    <div className={` ${css.conflictPopupContainer}  `}>
                        <div className={`flex ${css.crossIcon} ${css.cursorPointer}`}>
                            <div>
                                <ARIAButton
                                    ariaLabel="Close popup"
                                    onClick={() => toggleWarningDialog({ flag: false })}
                                >
                                    <img
                                        src={getAssetPath("svgs/icons/cross_18px_primary.svg")}
                                        alt={"cross icon"}
                                    />
                                </ARIAButton>
                            </div>
                        </div>
                        <div className="flex flexColumn alignCenter ">
                            <div className={`flex`}>
                                <ImageLoader
                                    src={warningDialog?.infoIconUrl}
                                    className={`marginTop-8 flex justifyCenter alignCenter`}
                                    height="46px"
                                    width={"46px"}
                                />
                            </div>

                            {warningDialog?.heading && (
                                <div className={`l4SemiBold marginTop-16`}>{warningDialog?.heading}</div>
                            )}
                            {warningDialog?.message && (
                                <div className={`bodyRegular marginTop-16 textSecondary `}>
                                    <span
                                        dangerouslySetInnerHTML={{
                                            __html: sanitizer(warningDialog?.message),
                                        }}
                                    />
                                </div>
                            )}
                            <div className="flexColumn alignCenter">
                                {warningDialog?.primaryCtaLabel && warningDialog?.primaryAction && (
                                    <ConditionalLink>
                                        <div
                                            className={`${css.popupButtonDimensions} flex justifyCenter marginTop-16 `}
                                        >
                                            <Button
                                                label={warningDialog?.primaryCtaLabel}
                                                onClick={warningDialog?.primaryAction}
                                            />
                                        </div>
                                    </ConditionalLink>
                                )}
                                {warningDialog?.secondaryCtaLabel && warningDialog?.secondaryAction && (
                                    <ConditionalLink>
                                        <div
                                            className={`${css.popupButtonDimensions} flex justifyCenter marginBoth-16 `}
                                        >
                                            <Button
                                                label={warningDialog?.secondaryCtaLabel}
                                                onClick={warningDialog?.secondaryAction}
                                                type={"secondary"}
                                            />
                                        </div>
                                    </ConditionalLink>
                                )}
                            </div>
                        </div>
                    </div>
                </Dialog>

                {mergeAccountInformation?.flag && (
                    <MergeAccount
                        forwardHandler={forwardHandler}
                        state={state}
                        localDispatch={localDispatch}
                    />
                )}
            </div>
        </>
    )
}

PhoneConflict.propTypes = {
    forwardHandler: PropTypes.func,
    onSuccess: PropTypes.func,
    state: PropTypes.shape({
        info: PropTypes.shape({
            phone: PropTypes.string,
            email: PropTypes.string,
        }),
        conflictInfo: PropTypes.shape({
            conflict: PropTypes.bool,
            number: PropTypes.string,
        }),
        mergeAccountInformation: PropTypes.shape({
            flag: PropTypes.bool,
        }),
    }).isRequired,
    localDispatch: PropTypes.func.isRequired,
}

export default PhoneConflict
